import { hot } from 'react-hot-loader/root';
import React from 'react';
import template from 'lodash/template';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import {
  spycraftMixedArrType,
  spycraftBasicArrType,
  spycraftPodcastArrType,
} from '../proptypes/spycraft';
import phrasesType from '../proptypes/phrases';
import PageWrapper from '../design-system/PageWrapper';
import PageSection from '../components/PageSection';
import SpycraftFeaturedCardDeck from '../components/SpycraftFeaturedCardDeck';
import PagedSpycraftCardDeck from '../components/PagedSpycraftCardDeck';
import PageHeading from '../components/PageHeading';
import UnderlineDivider from '../components/UnderlineDivider';
import { Header3 } from '../components/Typography';
import PhrasesContext from '../components/contexts/PhrasesContext';
import SpycraftPreviewDeck from '../components/SpycraftPreviewDeck';
import AccountSubNav from '../components/AccountSubNav';

const SpycraftPage = ({
  recommendations,
  featured,
  articles,
  podcasts,
  crosswords,
  videos,
  keyQualityNames,
  keyQualityNames: [
    keyQualityA,
    keyQualityB,
    keyQualityC,
  ],
  phrases: {
    pageTitle,
    introduction,
    recommendationsTitle,
    recommendationsTitleWithName,
    featuredTitle,
    articlesTitle,
    podcastsTitle,
    videosTitle,
    crosswordsTitle,
    moreCrosswordsButtonLabel,
    moreArticlesButtonLabel,
    moreVideosButtonLabel,
    morePodcastsButtonLabel,
    moreRecommendationsButtonLabel,
    playNextGame,
    progressBarLabel,
    playAllGames,
  },
  phrases,
  fullName,
  onboardingComplete,
  onboardingChallengesLength,
  onboardingChallengesPlayed,
  nextGamePath,
}) => {
  const introductionMd = keyQualityNames.length > 0
    ? <ReactMarkdown source={template(introduction)({ keyQualityA, keyQualityB, keyQualityC })} />
    : null;

  const header = fullName
    ? template(recommendationsTitleWithName)({ name: fullName })
    : recommendationsTitle;

  return (
    <PhrasesContext.Provider value={phrases}>
      <PageWrapper tallNav bgDark>
        <AccountSubNav />
        <PageSection theme="black">
          <PageHeading
            title={pageTitle}
            description={introductionMd}
          />
          <br />
          <Header3>{header}</Header3>
          <br />
          {onboardingComplete ? (
            <PagedSpycraftCardDeck
              content={recommendations}
              highlight
              showDescription={false}
              itemsPerPage={4}
              cardsPerRow={4}
              nextPageButtonLabel={moreRecommendationsButtonLabel}
            />
          ) : (
            <SpycraftPreviewDeck
              content={recommendations}
              progressBarLabel={progressBarLabel}
              onboardingChallengesLength={onboardingChallengesLength}
              onboardingChallengesPlayed={onboardingChallengesPlayed}
              playAllGames={playAllGames}
              playNextGame={playNextGame}
              nextGamePath={nextGamePath}
            />
          )}

          <UnderlineDivider />

          {featured.length === 3 && (
            <>
              <Header3>{featuredTitle}</Header3>
              <br />
              <SpycraftFeaturedCardDeck content={featured} />
              <UnderlineDivider />
            </>
          )}

          <Header3>{articlesTitle}</Header3>
          <br />
          <PagedSpycraftCardDeck
            content={articles}
            itemsPerPage={4}
            nextPageButtonLabel={moreArticlesButtonLabel}
          />
          <UnderlineDivider />
          <Header3>{podcastsTitle}</Header3>
          <br />
          <PagedSpycraftCardDeck
            content={podcasts}
            itemsPerPage={4}
            nextPageButtonLabel={morePodcastsButtonLabel}
          />
          <UnderlineDivider />
          <Header3>{videosTitle}</Header3>
          <br />
          <PagedSpycraftCardDeck
            content={videos}
            itemsPerPage={4}
            nextPageButtonLabel={moreVideosButtonLabel}
          />
          <UnderlineDivider />
          <Header3>{crosswordsTitle}</Header3>
          <br />
          <PagedSpycraftCardDeck
            content={crosswords}
            cardsPerRow={4}
            itemsPerPage={4}
            nextPageButtonLabel={moreCrosswordsButtonLabel}
          />
        </PageSection>
      </PageWrapper>
    </PhrasesContext.Provider>
  );
};

SpycraftPage.propTypes = {
  recommendations: spycraftMixedArrType.isRequired,
  featured: spycraftMixedArrType.isRequired,
  articles: spycraftBasicArrType.isRequired,
  podcasts: spycraftPodcastArrType.isRequired,
  videos: spycraftBasicArrType.isRequired,
  crosswords: spycraftBasicArrType.isRequired,
  keyQualityNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  fullName: PropTypes.string.isRequired,
  phrases: phrasesType.isRequired,
  onboardingComplete: PropTypes.bool.isRequired,
  onboardingChallengesLength: PropTypes.number.isRequired,
  onboardingChallengesPlayed: PropTypes.number.isRequired,
  nextGamePath: PropTypes.string.isRequired,
};

export default hot(SpycraftPage);
